import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Star, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { toast } from "sonner";

const T = {
  en: { title: "Rate your CHELA", sub: "How was your experience?", comment: "Anything to share? (optional)", send: "Submit rating", thanks: "Thank you for your rating!", again: "Thanks for helping us keep CHELA great.", notfound: "This rating link is not valid.", pick: "Please tap the stars to rate" },
  hi: { title: "अपने CHELA को रेटिंग दें", sub: "आपका अनुभव कैसा रहा?", comment: "कुछ कहना चाहें? (वैकल्पिक)", send: "रेटिंग भेजें", thanks: "रेटिंग के लिए धन्यवाद!", again: "CHELA को बेहतर बनाने में मदद के लिए शुक्रिया।", notfound: "यह रेटिंग लिंक सही नहीं है।", pick: "रेटिंग के लिए स्टार पर टैप करें" },
};

export default function Rate() {
  const { token } = useParams();
  const { lang, setLang } = useLang();
  const c = T[lang] || T.en;
  const [job, setJob] = useState(null);
  const [err, setErr] = useState(false);
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/jobs/${token}`).then((r) => { setJob(r.data); if (r.data.rating) setSent(true); }).catch(() => setErr(true));
  }, [token]);

  const send = async (n) => {
    const v = n || stars;
    if (!v) { toast.error(c.pick); return; }
    setBusy(true);
    try { await api.post(`/jobs/${token}/rating`, { stars: v, comment }); setSent(true); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] p-5 max-w-md mx-auto">
      <div className="flex items-center justify-between">
        <div className="font-display font-extrabold text-2xl text-[#121316]">CHELA</div>
        <button data-testid="rate-lang-toggle" onClick={() => setLang(lang === "hi" ? "en" : "hi")} className="h-9 px-4 rounded-full border border-[#E5E0D8] text-sm font-bold bg-white">{lang === "hi" ? "EN" : "हिंदी"}</button>
      </div>
      {err && <p data-testid="rate-not-found" className="mt-10 text-center text-[#5A5D65]">{c.notfound}</p>}
      {job && sent && (
        <div data-testid="rate-thanks" className="mt-10 rounded-3xl bg-white border border-[#E5E0D8] p-8 text-center">
          <CheckCircle2 size={44} className="mx-auto text-[#1B8A4B]" />
          <h1 className="mt-4 font-display font-extrabold text-2xl text-[#121316]">{c.thanks}</h1>
          <p className="mt-2 text-[#5A5D65]">{c.again}</p>
        </div>
      )}
      {job && !sent && (
        <div className="mt-8 rounded-3xl bg-white border border-[#E5E0D8] p-6 text-center">
          <h1 className="font-display font-extrabold text-2xl text-[#121316]">{c.title}</h1>
          <p className="mt-1 text-[#5A5D65]">{job.worker_name ? `${job.worker_name} · ` : ""}{job.service}</p>
          <p className="mt-4 font-semibold text-[#121316]">{c.sub}</p>
          <div className="mt-3 flex justify-center gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} data-testid={`rate-star-${n}`} onClick={() => setStars(n)} aria-label={`${n} stars`} className="p-1 transition-transform active:scale-90">
                <Star size={40} className={n <= stars ? "fill-[#F5A623] text-[#F5A623]" : "text-[#D8D3CB]"} />
              </button>
            ))}
          </div>
          <textarea data-testid="rate-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder={c.comment} rows={3} maxLength={500} className="mt-5 w-full p-4 rounded-xl border border-[#E5E0D8] outline-none focus:border-[#E05624] resize-none" />
          <button data-testid="rate-submit" onClick={() => send()} disabled={busy} className="mt-4 w-full h-14 rounded-full btn-saffron font-bold disabled:opacity-60">{c.send}</button>
        </div>
      )}
    </div>
  );
}
